import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const PHONE = "237699451241"; // +237 699 451 241 (no +, no spaces — WhatsApp wa.me format)
const DISPLAY = "+237 699 451 241";
const DEFAULT_MSG =
  "Hello Apostle Blaise Judah — I visited the Ministry website and would love to connect.";

export default function WhatsAppButton() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState(DEFAULT_MSG);
  const [pulse, setPulse] = useState(true);

  // Gentle attention pulse for the first 8 seconds after mount
  useEffect(() => {
    const t = setTimeout(() => setPulse(false), 8000);
    return () => clearTimeout(t);
  }, []);

  const openWA = () => {
    const url = `https://wa.me/${PHONE}?text=${encodeURIComponent(msg || DEFAULT_MSG)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      {/* Chat card */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[85] w-[calc(100vw-3rem)] max-w-[360px] border border-gold-500/30 bg-ink shadow-2xl overflow-hidden"
            data-testid="whatsapp-card"
          >
            {/* Header */}
            <div className="flex items-center gap-3 bg-[#0b8a4f] px-5 py-4 relative">
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
                <MessageCircle size={20} className="text-white" strokeWidth={1.8} />
              </div>
              <div className="flex-1">
                <p className="font-serif text-white text-base leading-tight">House of Blessing</p>
                <p className="text-[11px] text-white/80 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />
                  Typically replies within an hour
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="whatsapp-close"
                aria-label="Close WhatsApp chat"
                className="text-white/85 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body — chat bubble */}
            <div className="bg-ink-paper px-5 py-6">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold-400 mb-3">
                Chat on WhatsApp
              </p>
              <div className="bg-ink border border-gold-500/15 p-4 mb-4">
                <p className="text-sm text-white/85 leading-relaxed">
                  Peace and grace 🙏 <br />
                  Send us a message on WhatsApp — for prayer, ministry inquiries, partnership, book orders, or bookings.
                </p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.15em] text-gold-400">
                  {DISPLAY}
                </p>
              </div>

              <label className="block text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2">
                Your message
              </label>
              <textarea
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                rows={3}
                data-testid="whatsapp-message"
                className="w-full bg-ink border border-gold-500/20 text-white text-sm p-3 focus:outline-none focus:border-gold-400 resize-none"
              />

              <button
                onClick={openWA}
                data-testid="whatsapp-send"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#0b8a4f] hover:bg-[#0a7a45] text-white font-semibold py-3 text-sm uppercase tracking-[0.15em] transition-colors"
              >
                <Send size={14} /> Open WhatsApp
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="whatsapp-btn"
        aria-label={open ? "Close WhatsApp" : "Chat on WhatsApp"}
        className="fixed bottom-6 right-6 z-[86] w-14 h-14 rounded-full bg-[#0b8a4f] text-white shadow-2xl flex items-center justify-center hover:scale-105 hover:bg-[#0a7a45] active:scale-95 transition-transform duration-300"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
              <X size={22} />
            </motion.span>
          ) : (
            <motion.span key="w" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} transition={{ duration: 0.2 }}>
              <MessageCircle size={22} strokeWidth={1.8} />
            </motion.span>
          )}
        </AnimatePresence>
        {pulse && !open && (
          <span className="absolute inset-0 rounded-full bg-[#0b8a4f] opacity-40 animate-ping" aria-hidden />
        )}
      </button>
    </>
  );
}
